import { useEffect } from 'react';
import Lenis from 'lenis';

import { usePrefersReducedMotion } from './usePrefersReducedMotion';

// Sticky header height (rem 4 = 64px). Anchor jumps need to land
// below it, otherwise the destination heading is hidden behind the
// translucent bar.
const HEADER_OFFSET = 64;

/**
 * Boots Lenis smooth-scroll for the document and wires anchor links
 * (`<a href="#section">`) to scroll through it. Skips entirely when
 * the user has `prefers-reduced-motion: reduce` set, so accessibility
 * preference always wins over the "premium feel" of inertia.
 *
 * Call once at the App root.
 */
export function useSmoothScroll() {
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    if (reduce) return undefined;

    const lenis = new Lenis({
      // Soft, slightly long ramp — premium feel without feeling slow.
      duration: 1.15,
      // ease-out-expo: starts fast, glides into rest.
      easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
      smoothWheel: true,
      // Native touch scrolling on phones — `smoothWheel` already
      // ignores touch events, and forcing rAF interpolation on
      // touch can introduce drift on iOS Safari.
      touchMultiplier: 1.5,
    });

    let frameId = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      frameId = requestAnimationFrame(raf);
    };
    frameId = requestAnimationFrame(raf);

    // Anchor-link interception. Lenis' built-in `anchors` option
    // varies between versions; handling clicks ourselves keeps the
    // behaviour predictable and lets us apply the header offset.
    const onClick = (e: MouseEvent) => {
      // Bail for modified clicks (open-in-new-tab gestures).
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const link = (e.target as HTMLElement | null)?.closest?.('a');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#') || href === '#') return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target as HTMLElement, {
        offset: -HEADER_OFFSET,
        duration: 1.2,
      });
    };

    document.addEventListener('click', onClick);

    return () => {
      document.removeEventListener('click', onClick);
      cancelAnimationFrame(frameId);
      lenis.destroy();
    };
  }, [reduce]);
}
